import { useEffect, useRef, useState, type FormEvent } from "react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { Input } from "../ui/input";
import { Spinner } from "../ui/spinner";
import type { HostedProject } from "../../hosted/api";
import { HOSTED_PROJECT_NAME_MAX_LENGTH, validateHostedProjectName } from "../../hosted/hostedMode";
import { hostedApiErrorMessage } from "../../hosted/useHostedApiClient";

const NAME_INPUT_ID = "hosted-project-name";
const NAME_ERROR_ID = "hosted-project-name-error";

export interface HostedAddProjectFormProps {
  readonly onCreate: (name: string) => Promise<HostedProject>;
  readonly onCreated: (project: HostedProject) => void;
  readonly onCancel: () => void;
  /** Notifies the host when a request starts or ends so it can hold the dialog open. */
  readonly onSubmittingChange?: (isSubmitting: boolean) => void;
}

/**
 * The whole of hosted Add Project: one name field, Enter to submit through the
 * form, Escape/Cancel to dismiss. Kept separate from the dialog shell so the
 * field, error wiring, and submit path can be asserted without a portal.
 */
export function HostedAddProjectForm({
  onCreate,
  onCreated,
  onCancel,
  onSubmittingChange,
}: HostedAddProjectFormProps) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }
    const validation = validateHostedProjectName(name);
    if (validation.error) {
      setError(validation.error);
      inputRef.current?.focus();
      return;
    }

    setError(null);
    setIsSubmitting(true);
    void (async () => {
      try {
        const project = await onCreate(validation.value);
        setIsSubmitting(false);
        onCreated(project);
      } catch (cause) {
        // Keep the dialog open with the name intact: a rejected create is
        // usually retryable once the message is read.
        setIsSubmitting(false);
        setError(hostedApiErrorMessage(cause));
        inputRef.current?.focus();
      }
    })();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <DialogHeader>
        <DialogTitle>New Roblox project</DialogTitle>
        <DialogDescription>
          Projects live in your account. Pair Roblox Studio after creating one.
        </DialogDescription>
      </DialogHeader>
      <DialogPanel>
        <Field>
          <FieldLabel htmlFor={NAME_INPUT_ID}>Project name</FieldLabel>
          <Input
            autoFocus
            id={NAME_INPUT_ID}
            name="name"
            nativeInput
            maxLength={HOSTED_PROJECT_NAME_MAX_LENGTH}
            placeholder="Obby prototype"
            autoComplete="off"
            spellCheck={false}
            disabled={isSubmitting}
            aria-invalid={error !== null || undefined}
            aria-describedby={error ? NAME_ERROR_ID : undefined}
            ref={inputRef}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (error) setError(null);
            }}
          />
          {error ? (
            <p className="text-xs text-destructive-foreground" id={NAME_ERROR_ID} role="alert">
              {error}
            </p>
          ) : (
            <FieldDescription>You can rename it later.</FieldDescription>
          )}
        </Field>
      </DialogPanel>
      <DialogFooter>
        <Button type="button" variant="ghost" size="sm" disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? <Spinner className="size-4" /> : null}
          {isSubmitting ? "Creating…" : "Create project"}
        </Button>
      </DialogFooter>
    </form>
  );
}

/**
 * Hosted Add Project dialog. A Roblox project is a Worker-side record with a
 * name, so there is nothing to browse.
 */
export function HostedAddProjectDialog({
  open,
  onOpenChange,
  onCreate,
  onCreated,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreate: (name: string) => Promise<HostedProject>;
  readonly onCreated: (project: HostedProject) => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Hold the dialog while a create is in flight so its outcome stays visible.
        if (!nextOpen && isSubmitting) {
          return;
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogPopup className="max-w-md" data-testid="hosted-add-project-dialog">
        {/* Remounted per open so the field and any error start clean. */}
        <HostedAddProjectForm
          key={String(open)}
          onCreate={onCreate}
          onSubmittingChange={setIsSubmitting}
          onCancel={() => onOpenChange(false)}
          onCreated={(project) => {
            onOpenChange(false);
            onCreated(project);
          }}
        />
      </DialogPopup>
    </Dialog>
  );
}
